/**
 * Endpoints de **Finanzas → Cuadres** (`/api/finanzas/cuadres`).
 *
 * El listado trae, además de las filas paginadas, los contadores del módulo, los
 * meses del año sin cuadre activo, el resumen del año y los catálogos del
 * formulario (años y meses). La subida es `multipart` (el Excel viaja con el mes y
 * el año) y la descarga va con el nombre original del archivo.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CuerpoCuadre,
  DetalleCuadre,
  ExistenciaCuadre,
  FiltrosCuadres,
  IntegridadCuadre,
  ListadoCuadres,
} from './cuadres.types';

export const cuadresKeys = {
  todo: ['finanzas', 'cuadres'] as const,
  lista: (filtros: FiltrosCuadres) => ['finanzas', 'cuadres', 'lista', filtros] as const,
  detalle: (id: number) => ['finanzas', 'cuadres', 'detalle', id] as const,
  existe: (mes: number, anio: number) => ['finanzas', 'cuadres', 'existe', mes, anio] as const,
};

/** Cadena de consulta del listado. */
export function cadenaDeCuadres(filtros: FiltrosCuadres): string {
  const parametros = new URLSearchParams();

  if (filtros.anio) parametros.set('anio', String(filtros.anio));
  if (filtros.mes) parametros.set('mes', String(filtros.mes));
  if (filtros.estado) parametros.set('estado', filtros.estado);
  if (filtros.buscar) parametros.set('buscar', filtros.buscar);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

/** Formulario de la subida. */
function formularioDe(datos: CuerpoCuadre): FormData {
  const formulario = new FormData();

  formulario.set('mes', datos.mes);
  formulario.set('anio', datos.anio);
  formulario.set('observaciones', datos.observaciones);

  if (datos.archivo) formulario.set('archivo', datos.archivo);

  return formulario;
}

export const cuadresApi = {
  listar: (filtros: FiltrosCuadres, signal?: AbortSignal) =>
    apiClient.get<ListadoCuadres>(`/api/finanzas/cuadres${cadenaDeCuadres(filtros)}`, { signal }),

  detalle: (id: number, signal?: AbortSignal) =>
    apiClient.get<DetalleCuadre>(`/api/finanzas/cuadres/${id}`, { signal }),

  existe: (mes: number, anio: number) =>
    apiClient.get<ExistenciaCuadre>(`/api/finanzas/cuadres/existe?mes=${mes}&anio=${anio}`),

  subir: (datos: CuerpoCuadre) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/cuadres', {
      body: formularioDe(datos),
    }),

  validar: (id: number) =>
    apiClient.get<IntegridadCuadre>(`/api/finanzas/cuadres/${id}/validar`),

  anular: (id: number, motivo: string) =>
    apiClient.post<{ id: number; message: string }>(`/api/finanzas/cuadres/${id}/anular`, {
      body: { motivo },
    }),

  urlArchivo: (id: number) => `/api/finanzas/cuadres/${id}/archivo`,
};

/** Listado paginado con sus contadores y catálogos. */
export function useCuadres(filtros: FiltrosCuadres) {
  return useQuery({
    queryKey: cuadresKeys.lista(filtros),
    queryFn: ({ signal }) => cuadresApi.listar(filtros, signal),
    placeholderData: (anterior) => anterior,
  });
}

/** Detalle con la comprobación de integridad. */
export function useDetalleCuadre(id: number | null) {
  return useQuery({
    queryKey: cuadresKeys.detalle(id ?? 0),
    queryFn: ({ signal }) => cuadresApi.detalle(id as number, signal),
    enabled: Number(id) > 0,
  });
}

/** ¿Hay ya un cuadre activo de ese período? (aviso previo del formulario). */
export function useExisteCuadre(mes: number, anio: number) {
  return useQuery({
    queryKey: cuadresKeys.existe(mes, anio),
    queryFn: () => cuadresApi.existe(mes, anio),
    enabled: mes >= 1 && mes <= 12 && anio > 2000,
  });
}

/** Subida del cuadre. */
export function useSubirCuadre() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: CuerpoCuadre) => cuadresApi.subir(datos),
    onSuccess: () => cliente.invalidateQueries({ queryKey: cuadresKeys.todo }),
  });
}

/** Anulación del cuadre. */
export function useAnularCuadre() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => cuadresApi.anular(id, motivo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: cuadresKeys.todo }),
  });
}
